import { useState, useEffect, useRef, useCallback } from 'react';

// Generate unique ID for each hook instance
let instanceCounter = 0;

/**
 * Hook for managing tiered sound notifications with volume control
 *
 * Tiers:
 * - Tier 1: $1M - $25M
 * - Tier 2: $25M - $50M (yellow pulse)
 * - Tier 3: $50M - $100M (orange pulse)
 * - Tier 4: $100M+ (red/gold explosion)
 */
export default function useSoundNotifications() {
  // Create unique instance ID
  const instanceIdRef = useRef(++instanceCounter);

  // Load settings from localStorage
  const loadSettings = () => {
    try {
      const saved = localStorage.getItem('soundPreferences');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          volume: parsed.volume ?? 100,
          whaleTradesEnabled: parsed.whaleTradesEnabled ?? true,
          liquidationsEnabled: parsed.liquidationsEnabled ?? true,
          isMuted: parsed.isMuted ?? false,
          millionAlertEnabled: parsed.millionAlertEnabled ?? false, // $1M+ alert toggle
        };
      }
    } catch (error) {
      console.error('Error loading sound preferences:', error);
    }
    // Default settings
    return {
      volume: 100,
      whaleTradesEnabled: true,
      liquidationsEnabled: true,
      isMuted: false,
      millionAlertEnabled: false, // $1M+ alert off by default
    };
  };

  const [volume, setVolume] = useState(() => loadSettings().volume);
  const [whaleTradesEnabled, setWhaleTradesEnabled] = useState(() => loadSettings().whaleTradesEnabled);
  const [liquidationsEnabled, setLiquidationsEnabled] = useState(() => loadSettings().liquidationsEnabled);
  const [isMuted, setIsMuted] = useState(() => loadSettings().isMuted);
  const [millionAlertEnabled, setMillionAlertEnabled] = useState(() => loadSettings().millionAlertEnabled);

  // Refs for Audio objects (load once, reuse)
  const audioRefs = useRef({
    tier1: null,
    tier2: null,
    tier3: null,
    tier4: null,
  });

  // Web Audio API context for $1M+ melodic alerts
  const audioCtxRef = useRef(null);

  // Refs to always have current values (avoid closure issues)
  const volumeRef = useRef(volume);
  const isMutedRef = useRef(isMuted);
  const whaleTradesEnabledRef = useRef(whaleTradesEnabled);
  const liquidationsEnabledRef = useRef(liquidationsEnabled);
  const millionAlertEnabledRef = useRef(millionAlertEnabled);

  // Update refs when settings change
  useEffect(() => {
    volumeRef.current = volume;
    isMutedRef.current = isMuted;
    whaleTradesEnabledRef.current = whaleTradesEnabled;
    liquidationsEnabledRef.current = liquidationsEnabled;
    millionAlertEnabledRef.current = millionAlertEnabled;
  }, [volume, isMuted, whaleTradesEnabled, liquidationsEnabled, millionAlertEnabled]);

  // Initialize Audio objects on mount
  useEffect(() => {
    try {
      audioRefs.current = {
        tier1: new Audio('/assets/tier1-notification.mp3'),
        tier2: new Audio('/assets/tier2-notification.mp3'),
        tier3: new Audio('/assets/tier3-notification.mp3'),
        tier4: new Audio('/assets/tier4-notification.mp3'),
        unusualActivity: new Audio('/assets/unusual-activity-alert.mp3'),
      };

      // Preload audio files
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.preload = 'auto';
        }
      });

      // Initialize Web Audio API for $1M+ melodic alerts
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();

    } catch (error) {
      console.error('❌ Error initializing sound notifications:', error);
    }

    // Cleanup
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
      // Close Web Audio context
      if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
        audioCtxRef.current.close().catch(() => {});
      }
    };
  }, []);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    const settings = {
      volume,
      whaleTradesEnabled,
      liquidationsEnabled,
      isMuted,
      millionAlertEnabled,
    };
    localStorage.setItem('soundPreferences', JSON.stringify(settings));
  }, [volume, whaleTradesEnabled, liquidationsEnabled, isMuted, millionAlertEnabled]);

  /**
   * Determine tier based on dollar size
   * @param {number} dollarSize - Trade size in USD
   * @returns {1|2|3|4|null} Tier number or null if < $1M
   */
  const getTier = useCallback((dollarSize) => {
    if (dollarSize < 1_000_000) return null; // No notification for < $1M
    if (dollarSize < 25_000_000) return 1;   // $1M - $25M
    if (dollarSize < 50_000_000) return 2;   // $25M - $50M
    if (dollarSize < 100_000_000) return 3;  // $50M - $100M
    return 4;                                 // $100M+
  }, []);

  /**
   * Play tiered sound notification
   * @param {number} dollarSize - Trade size in USD
   * @param {'whale'|'liquidation'|'signal'} tradeType - Type of trade
   * @param {{coin?: string, side?: 'buy'|'sell', exchange?: string}} [meta]
   *   Optional context — when present, a `tradermap:trade-alert` CustomEvent
   *   is dispatched on window so a UI layer (AlertToast) can show *which*
   *   coin triggered the sound. Reported by Chill: users hear the alert but
   *   can't tell which asset it's for.
   * @returns {1|2|3|4|null} Tier that was played, or null if not played
   */
  const playTieredSound = useCallback((dollarSize, tradeType, meta) => {
    // ✅ Use refs instead of state to avoid closure issues!
    const currentVolume = volumeRef.current;
    const currentMuted = isMutedRef.current;
    const currentWhaleEnabled = whaleTradesEnabledRef.current;
    const currentLiqEnabled = liquidationsEnabledRef.current;

    // Check if sound should be played
    if (currentMuted) {
      return null;
    }

    // Check if this trade type is enabled
    if (tradeType === 'whale' && !currentWhaleEnabled) {
      return null;
    }
    if (tradeType === 'liquidation' && !currentLiqEnabled) {
      return null;
    }

    // Cross-coin signal: fixed lower-volume tier-1 cue, ignores dollarSize.
    if (tradeType === 'signal') {
      try {
        const audio = audioRefs.current.tier1;
        if (!audio) return null;
        audio.currentTime = 0;
        // Lower fixed volume so signals don't drown out trade alerts.
        audio.volume = (currentVolume / 100) * 0.6;
        audio.play().catch(error => {
          console.error('❌ Error playing signal sound:', error);
        });
        return 1;
      } catch (error) {
        console.error('❌ Error in playTieredSound (signal):', error);
        return null;
      }
    }

    // Determine tier
    const tier = getTier(dollarSize);
    if (tier === null) {
      return null;
    }

    // Play sound
    try {
      const audio = audioRefs.current[`tier${tier}`];
      if (!audio) {
        console.error('❌ Audio not found for tier', tier);
        return null;
      }

      // Reset audio to start if already playing
      audio.currentTime = 0;

      // ✅ Set volume using ref value (0-100 converted to 0-1)
      const volumeLevel = currentVolume / 100;
      audio.volume = volumeLevel;

      // Play
      audio.play().catch(error => {
        console.error('❌ Error playing sound:', error);
      });

      // Broadcast a UI hook so a toast / badge layer can show what just
      // triggered the sound. No-op when nothing is listening.
      if (typeof window !== 'undefined') {
        try {
          window.dispatchEvent(new CustomEvent('tradermap:trade-alert', {
            detail: {
              kind: tradeType,
              tier,
              dollarSize,
              coin: meta?.coin || null,
              side: meta?.side || null,
              exchange: meta?.exchange || null,
              t: Date.now(),
            },
          }));
        } catch {}
      }

      return tier;
    } catch (error) {
      console.error('❌ Error in playTieredSound:', error);
      return null;
    }
  }, [getTier]); // ✅ Only getTier in dependencies - all settings use refs!

  /**
   * Play unusual activity alert sound
   */
  const playUnusualActivityAlert = useCallback(() => {
    const currentVolume = volumeRef.current;
    const currentMuted = isMutedRef.current;

    if (currentMuted) {
      return;
    }

    try {
      const audio = audioRefs.current.unusualActivity;
      if (!audio) {
        console.error('❌ Unusual activity audio not found');
        return;
      }

      // Reset audio to start if already playing
      audio.currentTime = 0;

      // Set volume using ref value (0-100 converted to 0-1)
      const volumeLevel = currentVolume / 100;
      audio.volume = volumeLevel;

      // Play
      audio.play().catch(error => {
        console.error('❌ Error playing unusual activity sound:', error);
      });

    } catch (error) {
      console.error('❌ Error in playUnusualActivityAlert:', error);
    }
  }, []);

  /**
   * Play a single tone using Web Audio API
   * @param {AudioContext} ctx - Audio context
   * @param {number} freq - Frequency in Hz
   * @param {string} type - Oscillator type ('sine', 'triangle', etc.)
   * @param {number} startOffset - Start time offset in seconds
   * @param {number} duration - Duration in seconds
   * @param {number} volume - Volume (0-1)
   */
  const playTone = useCallback((ctx, freq, type, startOffset, duration, volume = 0.05) => {
    const t = ctx.currentTime + startOffset;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(volume, t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(t);
    osc.stop(t + duration);

    // Disconnect nodes after playback to prevent accumulation
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
  }, []);

  /**
   * Play Long/Short melodic sound using Web Audio API
   * Long (buy): Rapid Major Triad (C6, E6, G6) - positive, energetic
   * Short (sell): Falling Minor 2nd (Eb4, D4) - tense, falling
   * @param {'buy'|'sell'} side - Trade side (buy = long, sell = short)
   * @param {number} dollarSize - Trade size in USD (for logging)
   * @returns {boolean} Whether sound was played
   */
  const playMillionAlert = useCallback((side, dollarSize, meta) => {
    const currentMillionEnabled = millionAlertEnabledRef.current;
    const currentVolume = volumeRef.current;
    const currentMuted = isMutedRef.current;

    // Check if Long/Short sound is enabled
    if (!currentMillionEnabled) {
      return false;
    }

    // Check if muted
    if (currentMuted) {
      return false;
    }

    // Broadcast for the toast layer (same channel as playTieredSound).
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('tradermap:trade-alert', {
          detail: {
            kind: 'million',
            tier: null,
            dollarSize,
            coin: meta?.coin || null,
            side,
            exchange: meta?.exchange || null,
            t: Date.now(),
          },
        }));
      } catch {}
    }

    try {
      const ctx = audioCtxRef.current;
      if (!ctx) {
        console.error('❌ AudioContext not initialized');
        return false;
      }

      // Resume audio context if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      // Scale volume: user volume (0-100) -> base volume adjustment
      // Base volumes from reference: 0.02-0.05
      // Scale by user volume percentage
      const volumeScale = (currentVolume / 100) * 0.8; // Max ~0.8 multiplier

      if (side === 'buy') {
        // LONG: Rapid Major Triad (C6, E6, G6) - positive, energetic
        const base = 1046.50; // C6
        playTone(ctx, base, 'sine', 0, 0.05, 0.04 * volumeScale);
        playTone(ctx, base * 1.25, 'sine', 0.015, 0.05, 0.03 * volumeScale); // E6
        playTone(ctx, base * 1.5, 'sine', 0.03, 0.05, 0.02 * volumeScale);  // G6
      } else {
        // SHORT: Falling Minor 2nd Tension (Eb4, D4) - tense, falling
        playTone(ctx, 311.13, 'triangle', 0, 0.08, 0.05 * volumeScale); // Eb4
        playTone(ctx, 293.66, 'triangle', 0.02, 0.08, 0.04 * volumeScale); // D4
      }

      return true;
    } catch (error) {
      console.error('❌ Error in playMillionAlert:', error);
      return false;
    }
  }, [playTone]);

  /**
   * Toggle mute
   */
  const toggleMute = useCallback(() => {
    setIsMuted(prev => !prev);
  }, []);

  /**
   * Toggle whale trades sound
   */
  const toggleWhaleTradesSound = useCallback(() => {
    setWhaleTradesEnabled(prev => {
      const newValue = !prev;
      const currentVolume = volumeRef.current;
      const currentMuted = isMutedRef.current;
      // If enabling sound but volume is 0 OR muted, dispatch warning event
      if (newValue && (currentVolume === 0 || currentMuted)) {
        window.dispatchEvent(new CustomEvent('volumeWarning'));
      }
      return newValue;
    });
  }, []);

  /**
   * Toggle liquidations sound
   */
  const toggleLiquidationsSound = useCallback(() => {
    setLiquidationsEnabled(prev => {
      const newValue = !prev;
      const currentVolume = volumeRef.current;
      const currentMuted = isMutedRef.current;
      // If enabling sound but volume is 0 OR muted, dispatch warning event
      if (newValue && (currentVolume === 0 || currentMuted)) {
        window.dispatchEvent(new CustomEvent('volumeWarning'));
      }
      return newValue;
    });
  }, []);

  /**
   * Set volume (0-100)
   */
  const setVolumeLevel = useCallback((newVolume) => {
    const clampedVolume = Math.max(0, Math.min(100, newVolume));
    setVolume(clampedVolume);
  }, []);

  /**
   * Toggle $1M+ alert
   */
  const toggleMillionAlert = useCallback(() => {
    setMillionAlertEnabled(prev => !prev);
  }, []);

  return {
    // State
    volume,
    whaleTradesEnabled,
    liquidationsEnabled,
    isMuted,
    millionAlertEnabled,

    // Actions
    playTieredSound,
    playUnusualActivityAlert,
    playMillionAlert,
    getTier,
    setVolumeLevel,
    toggleMute,
    toggleWhaleTradesSound,
    toggleLiquidationsSound,
    toggleMillionAlert,
  };
}
